import React, { Fragment, useEffect, useRef, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";

import { StorePurchaseBill } from "../../../../api/medicalStore/transaction/purchaseBillAPI";
import { GetDepartments } from "../../../../api/masters/departmentAPI";
import { GetCategoriesMedicalStore } from "../../../../api/medicalStore/master/categoryMedicalStoreAPI";
import { GetItems } from "../../../../api/medicalStore/master/itemAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

/* ================= VALIDATION ================= */
const schema = yup.object().shape({
	vrType: yup.string().required("Voucher Type is required"),
	vrNo: yup.number().required("Voucher No is required"),
	vrDate: yup.date().required("Voucher Date is required"),
	supplierName: yup.string().required("Supplier Name is required"),
	purchaseAccount: yup.number().required("Purchase Account is required"),
	partyBillNo: yup.string().required("Party Bill No is required"),
	billDate: yup.date().required("Bill Date is required"),
	departmentId: yup.string().required("Department is required"),
	gstType: yup.string().required("GST Type is required"),

	totalNoOfPacks: yup.number().required(),
	grossAmount: yup.number().required(),
	gst: yup.number().required(),
	additionPercentage: yup.number().required(),
	additionAmount: yup.number().required(),
	discountPercentage: yup.number().required(),
	discountAmount: yup.number().required(),
	roundOff: yup.number().required(),
	netAmount: yup.number().required(),

	productDetails: yup.array().of(
		yup.object().shape({
			categoryId: yup.string().required(),
			productId: yup.string().required(),
			expiryDate: yup.date().required(),
			batchNo: yup.number().required(),
			noOfPacks: yup.number().required(),
			freeQty: yup.number().required(),
			mrp: yup.number().required(),
			amount: yup.number().required(),
			discountPercentage: yup.number().required(),
			discountAmount: yup.number().required(),
			gstPercentage: yup.number().required(),
			gstAmount: yup.number().required(),
			totalAmount: yup.number().required()
		})
	)
});

const AddPurchaseBillEntry = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [departments, setDepartments] = useState([]);
	const [categories, setCategories] = useState([]);
	const [items, setItems] = useState([]);

	const {
		register,
		control,
		setValue,
		getValues,
		handleSubmit,
		formState: { errors }
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			productDetails: [
				{
					categoryId: "",
					productId: "",
					expiryDate: "",
					batchNo: "",
					noOfPacks: "",
					freeQty: "",
					mrp: "",
					amount: "",
					discountPercentage: "",
					discountAmount: "",
					gstPercentage: "",
					gstAmount: "",
					totalAmount: ""
				}
			]
		}
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "productDetails"
	});

	useEffect(() => {
		GetDepartments().then(r => r.success && setDepartments(r.data));
		GetCategoriesMedicalStore().then(r => r.success && setCategories(r.data));
		GetItems().then(r => r.success && setItems(r.data));
	}, []);

	const calculateRow = index => {
		const packs = Number(getValues(`productDetails.${index}.noOfPacks`) || 0);
		const rate = Number(getValues(`productDetails.${index}.purchaseRate`) || 0);
		const discountPct = Number(
			getValues(`productDetails.${index}.discountPercentage`) || 0
		);
		const gstPct = Number(
			getValues(`productDetails.${index}.gstPercentage`) || 0
		);

		const amount = packs * rate;
		const discountAmt = amount * discountPct / 100;
		const taxable = amount - discountAmt;
		const gstAmt = taxable * gstPct / 100;
		const totalAmount = taxable + gstAmt;

		setValue(`productDetails.${index}.amount`, amount.toFixed(2));
		setValue(`productDetails.${index}.discountAmount`, discountAmt.toFixed(2));
		setValue(`productDetails.${index}.gstAmount`, gstAmt.toFixed(2));
		setValue(`productDetails.${index}.totalAmount`, totalAmount.toFixed(2));
	};

	const watchedProducts = useWatch({ control, name: "productDetails" });

	useEffect(() => {
		if (!watchedProducts?.length) return;

		let totalPacks = 0;
		let grossAmount = 0;
		let totalGST = 0;
		let totalDiscount = 0;

		watchedProducts.forEach(item => {
			totalPacks += Number(item.noOfPacks || 0);
			grossAmount += Number(item.amount || 0);
			totalGST += Number(item.gstAmount || 0);
			totalDiscount += Number(item.discountAmount || 0);
		});

		const netAmount = grossAmount - totalDiscount + totalGST;

		setValue("totalNoOfPacks", totalPacks, { shouldDirty: false });
		setValue("grossAmount", grossAmount.toFixed(2), { shouldDirty: false });
		setValue("gst", totalGST.toFixed(2), { shouldDirty: false });
		setValue("discountAmount", totalDiscount.toFixed(2), {
			shouldDirty: false
		});
		setValue("netAmount", netAmount.toFixed(2), { shouldDirty: false });
	}, [watchedProducts, setValue]);

	const grossAmount = useWatch({ control, name: "grossAmount" });
	const gst = useWatch({ control, name: "gst" });
	const additionPercentage = useWatch({ control, name: "additionPercentage" });
	const discountPercentage = useWatch({ control, name: "discountPercentage" });
	const roundOff = useWatch({ control, name: "roundOff" });

	useEffect(() => {
		const g = Number(grossAmount || 0);
		const gstAmt = Number(gst || 0);
		const addPer = Number(additionPercentage || 0);
		const disPer = Number(discountPercentage || 0);
		const ro = Number(roundOff || 0);

		const addAmt = (g * addPer) / 100;
		const billDisAmt = (g * disPer) / 100;
		const netAmt = g + gstAmt + addAmt - billDisAmt;

		setValue("additionAmount", addAmt.toFixed(2), { shouldDirty: false });
		setValue("discountAmount", billDisAmt.toFixed(2), {
			shouldDirty: false
		});
		setValue("netAmount", netAmt.toFixed(ro), { shouldDirty: false });
	}, [
		grossAmount,
		gst,
		additionPercentage,
		discountPercentage,
		roundOff,
		setValue
	]);


	/* ================= SUBMIT ================= */
	const onSubmit = async data => {
		dispatch({ type: "LOADER_START" });
		try {
			const response = await StorePurchaseBill(data);
			if (response.success) {
				showToast("success", response.message);
				navigate(
					`${process.env
						.PUBLIC_URL}/medical-store/transaction/purchase-bill-entry/list`
				);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		} finally {
			dispatch({ type: "LOADER_STOP" });
		}
	};

	const formRef = useRef(null);
		  useKeyboardSubmit(handleSubmit, onSubmit);
		  useEnterNavigation(formRef);

	return (
		<Fragment>
			<Breadcrumb parent="Medical Store" title="Add Purchase Bill" />

			<div className="container-fluid">
				<div className="card">
					{/* HEADER */}
					<div className="card-header d-flex justify-content-between">
						<h5>Add Purchase Bill</h5>
						<Link
							to={`${process.env
								.PUBLIC_URL}/medical-store/transaction/purchase-bill-entry/list`}
						>
							<button className="btn btn-primary" id="list">
								List Purchase Bill
							</button>
							<UncontrolledTooltip target="list">
								List Purchase Bills
							</UncontrolledTooltip>
						</Link>
					</div>

					{/* FORM */}
					<form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
						<div className="card-body">
							{/* ================= HEADER DETAILS ================= */}
							<div className="row">
								<div className="col-3">
									<label>Voucher Type</label>
									<input className="form-control" {...register("vrType")} />
									{errors.vrType &&
										<span className="text-danger">
											{errors.vrType.message}
										</span>}
								</div>
								<div className="col-3">
									<label>Voucher No</label>
									<input
										type="number" step="any"
										className="form-control"
										{...register("vrNo")}
									/>
									{errors.vrNo &&
										<span className="text-danger">
											{errors.vrNo.message}
										</span>}
								</div>
								<div className="col-3">
									<label>Voucher Date</label>
									<input
										type="date"
										className="form-control"
										{...register("vrDate")}
									/>
									{errors.vrDate &&
										<span className="text-danger">
											{errors.vrDate.message}
										</span>}
								</div>
								<div className="col-3">
									<label>GST Type</label>
									<select className="form-control" {...register("gstType")}>
										<option value="">Select</option>
										<option value="CGST_SGST">CGST + SGST</option>
										<option value="IGST">IGST</option>
									</select>
									{errors.gstType &&
										<span className="text-danger">
											{errors.gstType.message}
										</span>}
								</div>
							</div>

							<div className="row mt-3">
								<div className="col-4">
									<label>Supplier Name</label>
									<input
										className="form-control"
										{...register("supplierName")}
									/>
									{errors.supplierName &&
										<span className="text-danger">
											{errors.supplierName.message}
										</span>}
								</div>
								<div className="col-4">
									<label>Purchase Account</label>
									<input
										type="number" step="any"
										className="form-control"
										{...register("purchaseAccount")}
									/>
									{errors.purchaseAccount &&
										<span className="text-danger">
											{errors.purchaseAccount.message}
										</span>}
								</div>
								<div className="col-4">
									<label>Department</label>
									<select
										className="form-control"
										{...register("departmentId")}
									>
										<option value="">Select</option>
										{departments.map(department =>
											<option key={department._id} value={department._id}>
												{department.name}
											</option>
										)}
									</select>
									{errors.departmentId &&
										<span className="text-danger">
											{errors.departmentId.message}
										</span>}
								</div>
							</div>

							<div className="row mt-3">
								<div className="col-6">
									<label>Party Bill No</label>
									<input
										className="form-control"
										{...register("partyBillNo")}
									/>
									{errors.partyBillNo &&
										<span className="text-danger">
											{errors.partyBillNo.message}
										</span>}
								</div>
								<div className="col-6">
									<label>Bill Date</label>
									<input
										type="date"
										className="form-control"
										{...register("billDate")}
									/>
									{errors.billDate &&
										<span className="text-danger">
											{errors.billDate.message}
										</span>}
								</div>
							</div>

							{/* ================= PRODUCT DETAILS ================= */}
							<hr />
							<h6>Product Details</h6>

							{fields.map((item, index) =>
								<div className="row border p-2 mb-2" key={item.id}>
									<div className="col-2">
										<label>Category</label>
										<select
											placeholder="Category"
											className="form-control"
											{...register(`productDetails.${index}.categoryId`)}
										>
											<option value="">Select</option>
											{categories.map(category =>
												<option key={category._id} value={category._id}>
													{category.name}
												</option>
											)}
										</select>
										{errors[`productDetails.${index}.categoryId`] &&
											<span className="text-danger">
												{errors[`productDetails.${index}.categoryId`].message}
											</span>}
									</div>
									<div className="col-2">
										<label>Product</label>
										<select
											placeholder="Product"
											className="form-control"
											{...register(`productDetails.${index}.productId`)}
										>
											<option value="">Select</option>
											{items.map(item =>
												<option key={item._id} value={item._id}>
													{item.name}
												</option>
											)}
										</select>
										{errors[`productDetails.${index}.productId`] &&
											<span className="text-danger">
												{errors[`productDetails.${index}.productId`].message}
											</span>}
									</div>
									<div className="col-2">
										<label>Expiry Date</label>
										<input
											type="date"
											className="form-control"
											{...register(`productDetails.${index}.expiryDate`)}
										/>
										{errors[`productDetails.${index}.expiryDate`] &&
											<span className="text-danger">
												{errors[`productDetails.${index}.expiryDate`].message}
											</span>}
									</div>
									<div className="col-2">
										<label>Batch No</label>
										<input
											placeholder="Batch"
											type="number" step="any"
											className="form-control"
											{...register(`productDetails.${index}.batchNo`)}
										/>
										{errors[`productDetails.${index}.batchNo`] &&
											<span className="text-danger">
												{errors[`productDetails.${index}.batchNo`].message}
											</span>}
									</div>
									<div className="col-2">
										<label>No. of Packs</label>
										<input
											placeholder="Packs"
											type="number" step="any"
											className="form-control"
											{...register(`productDetails.${index}.noOfPacks`, {
												onChange: () => calculateRow(index)
											})}
										/>
										{errors[`productDetails.${index}.noOfPacks`] &&
											<span className="text-danger">
												{errors[`productDetails.${index}.noOfPacks`].message}
											</span>}
									</div>
									<div className="col-2">
										<label>Purchase Rate</label>
										<input
											placeholder="Purchase Rate"
											type="number" step="any"
											className="form-control"
											{...register(`productDetails.${index}.purchaseRate`, {
												onChange: () => calculateRow(index)
											})}
										/>
										{errors[`productDetails.${index}.purchaseRate`] &&
											<span className="text-danger">
												{errors[`productDetails.${index}.purchaseRate`].message}
											</span>}
									</div>
									<div className="col-2">
										<label>Free Qty</label>
										<input
											placeholder="Free Qty"
											type="number" step="any"
											className="form-control"
											{...register(`productDetails.${index}.freeQty`)}
										/>
										{errors[`productDetails.${index}.freeQty`] &&
											<span className="text-danger">
												{errors[`productDetails.${index}.freeQty`].message}
											</span>}
									</div>
									<div className="col-2">
										<label>MRP</label>
										<input
											placeholder="MRP"
											type="number" step="any"
											className="form-control"
											{...register(`productDetails.${index}.mrp`)}
										/>
										{errors[`productDetails.${index}.mrp`] &&
											<span className="text-danger">
												{errors[`productDetails.${index}.mrp`].message}
											</span>}
									</div>
									<div className="col-2">
										<label>Amount</label>
										<input
											placeholder="Amount"
											type="number" step="any"
											className="form-control"
											{...register(`productDetails.${index}.amount`)}
										/>
										{errors[`productDetails.${index}.amount`] &&
											<span className="text-danger">
												{errors[`productDetails.${index}.amount`].message}
											</span>}
									</div>
									<div className="col-2">
										<label>Discount Percent</label>
										<input
											placeholder="Discount Percent"
											type="number" step="any"
											className="form-control"
											{...register(
												`productDetails.${index}.discountPercentage`,
												{
													onChange: () => calculateRow(index)
												}
											)}
										/>
										{errors[`productDetails.${index}.discountPercentage`] &&
											<span className="text-danger">
												{
													errors[`productDetails.${index}.discountPercentage`]
														.message
												}
											</span>}
									</div>
									<div className="col-2">
										<label>Discount Amount</label>
										<input
											placeholder="Discount Amount"
											type="number" step="any"
											className="form-control"
											{...register(`productDetails.${index}.discountAmount`)}
										/>
										{errors[`productDetails.${index}.discountAmount`] &&
											<span className="text-danger">
												{
													errors[`productDetails.${index}.discountAmount`]
														.message
												}
											</span>}
									</div>
									<div className="col-2">
										<label>GST Percent</label>
										<input
											placeholder="GST Percent"
											type="number" step="any"
											className="form-control"
											{...register(`productDetails.${index}.gstPercentage`, {
												onChange: () => calculateRow(index)
											})}
										/>
										{errors[`productDetails.${index}.gstPercentage`] &&
											<span className="text-danger">
												{
													errors[`productDetails.${index}.gstPercentage`]
														.message
												}
											</span>}
									</div>
									<div className="col-2">
										<label>GST Amount</label>
										<input
											placeholder="GST Amount"
											type="number" step="any"
											className="form-control"
											{...register(`productDetails.${index}.gstAmount`)}
										/>
										{errors[`productDetails.${index}.gstAmount`] &&
											<span className="text-danger">
												{errors[`productDetails.${index}.gstAmount`].message}
											</span>}
									</div>
									<div className="col-2">
										<label>Total Amount</label>
										<input
											placeholder="Total Amount"
											type="number" step="any"
											className="form-control"
											{...register(`productDetails.${index}.totalAmount`)}
										/>
										{errors[`productDetails.${index}.totalAmount`] &&
											<span className="text-danger">
												{errors[`productDetails.${index}.totalAmount`].message}
											</span>}
									</div>
									<div className="col-1 mt-3">
										<button
											type="button"
											className="btn btn-danger"
											onClick={() => remove(index)}
										>
											✕
										</button>
									</div>
								</div>
							)}

							<button
								type="button"
								className="btn btn-secondary"
								onClick={() => append({})}
							>
								+ Add Product
							</button>

							{/* ================= SUMMARY ================= */}
							<hr />
							<div className="row">
								<div className="col-3">
									<label>Total No Of Packs</label>
									<input
										type="number" step="any"
										className="form-control"
										{...register("totalNoOfPacks")}
									/>
									{errors.totalNoOfPacks &&
										<span className="text-danger">
											{errors.totalNoOfPacks.message}
										</span>}
								</div>
								<div className="col-3">
									<label>GST</label>
									<input
										type="number" step="any"
										className="form-control"
										{...register("gst")}
									/>
									{errors.gst &&
										<span className="text-danger">
											{errors.gst.message}
										</span>}
								</div>
								<div className="col-3">
									<label>Gross Amount</label>
									<input
										type="number" step="any"
										className="form-control"
										{...register("grossAmount")}
									/>
									{errors.grossAmount &&
										<span className="text-danger">
											{errors.grossAmount.message}
										</span>}
								</div>

								<div className="col-3">
									<label>Addition %</label>
									<input
										type="number" step="any"
										className="form-control"
										{...register("additionPercentage")}
									/>
									{errors.additionPercentage &&
										<span className="text-danger">
											{errors.additionPercentage.message}
										</span>}
								</div>
								<div className="col-3">
									<label>Addition Amount</label>
									<input
										type="number" step="any"
										className="form-control"
										{...register("additionAmount")}
									/>
									{errors.additionAmount &&
										<span className="text-danger">
											{errors.additionAmount.message}
										</span>}
								</div>
								<div className="col-3">
									<label>Discount %</label>
									<input
										type="number" step="any"
										className="form-control"
										{...register("discountPercentage")}
									/>
									{errors.discountPercentage &&
										<span className="text-danger">
											{errors.discountPercentage.message}
										</span>}
								</div>
								<div className="col-3">
									<label>Discount Amount</label>
									<input
										type="number" step="any"
										className="form-control"
										{...register("discountAmount")}
									/>
									{errors.discountAmount &&
										<span className="text-danger">
											{errors.discountAmount.message}
										</span>}
								</div>
								<div className="col-3">
									<label>Round Off</label>
									<input
										type="number" step="any"
										className="form-control"
										{...register("roundOff")}
									/>
									{errors.roundOff &&
										<span className="text-danger">
											{errors.roundOff.message}
										</span>}
								</div>
								<div className="col-3">
									<label>Net Amount</label>
									<input
										type="number" step="any"
										className="form-control"
										{...register("netAmount")}
									/>
									{errors.netAmount &&
										<span className="text-danger">
											{errors.netAmount.message}
										</span>}
								</div>
							</div>
						</div>

						{/* FOOTER */}
						<div className="card-footer text-end">
							<button className="btn btn-primary" type="submit">
								Submit Purchase Bill
							</button>
						</div>
					</form>
				</div>
			</div>
		</Fragment>
	);
};

export default AddPurchaseBillEntry;
